import type {
  DataDrivenPropertyValueSpecification,
  ExpressionSpecification,
  LayerSpecification,
  StyleSpecification,
} from 'maplibre-gl';

/**
 * pmtiles (オフライン地図) 用ベクタースタイルビルダー (docs/107 オフライン残 / 2026-06-02 拡張)。
 *
 * MapViewImpl / AnalysisHeatmapView / IdeaWorkspaceMiniMap が共用する。当初は最小スタイル
 * (background / earth / water / roads + 地名) だったが、「建物が地面に隠れて見えない / 施設名が
 * 出ない / 要素が乏しい」を解消するため、**正しい描画順の完全ベースマップ**へ拡張した:
 *   背景 → 土地被覆(緑) → 公園 → 土地利用 → 水域 → 水路 → 建物 → 道路(縁取り→本体)
 *   → 道路名 → 地名 → POI/施設名。fill を下、line を中、label を上に積むのが要点。
 *
 * 採用方針:
 *   - **glyphs = Protomaps glyphs (CDN)**。vite.config の PWA workbox `runtimeCaching`
 *     (`protomaps.github.io/basemaps-assets`) で CacheFirst 済のため、一度 online で開けば
 *     offline でも日本語地名フォントが解決できる。
 *   - 外部テーマ依存 (protomaps-themes-base 等) は入れず、必要 layer を hand-author する。
 *
 * ★ schema 対応 (R-35.2): source-layer 名は schema で異なるため `buildPmtilesStyle` は schema で
 *   `omtLayers()` / `protomapsLayers()` を出し分ける。国内地区は staging/prod とも OMT が既定経路。
 *   - omt (OpenMapTiles): landcover / park / landuse / water / waterway / building /
 *       transportation(+_name) / place / poi。rank が小さいほど主要。
 *   - protomaps: earth / landuse / water / buildings / roads / places / pois。rank は `pmap:rank`。
 *   注: source-layer 名が pmtiles 側に無い layer は MapLibre が throw せず無描画 skip するため、
 *   schema 不一致だと「エラーは出ないのに真っ白」になる (描画されない原因の筆頭)。
 */

/** pmtiles vector source の id。 */
export const PMTILES_SOURCE = 'pmtiles-base';

/** pmtiles の vector schema 種別。generatePmtiles が district.mapPmtiles.schema に記録する値。 */
export type PmtilesSchema = 'protomaps' | 'omt';

/**
 * Protomaps glyphs (font PBF) の CDN テンプレート。
 * vite.config の workbox runtimeCaching (`protomaps.github.io/basemaps-assets`) で CacheFirst 済。
 */
export const PROTOMAPS_GLYPHS =
  'https://protomaps.github.io/basemaps-assets/fonts/{fontstack}/{range}.pbf';

const PMTILES_ATTRIBUTION =
  '© <a href="https://protomaps.com">Protomaps</a> © <a href="https://openstreetmap.org">OSM</a>';

/**
 * 地名ラベルに使う font stack。Protomaps basemaps-assets が配信する font。
 * "Noto Sans Regular" は CJK を含むため日本語地名も描画可能 (CDN 側に PBF が存在)。
 */
const LABEL_FONT = ['Noto Sans Regular'];

/**
 * 端末の言語 (navigator.language の primary subtag、例 'ja' / 'en' / 'zh')。
 * 取得不可 (SSR / 制限環境) は '' を返し、呼び出し側で「日本語 + 英語のみ」にフォールバックする。
 */
export function resolveLabelLang(): string {
  if (
    typeof navigator !== 'undefined' &&
    typeof navigator.language === 'string' &&
    navigator.language
  ) {
    return navigator.language.split('-')[0]!.toLowerCase();
  }
  return '';
}

/**
 * ラベルの text-field を端末言語に合わせて構築する (多言語混在の抑制)。
 *
 * ★ `name:latin` / `name:nonlatin` は **使わない**。Planetiler の OMT は日本語漢字を ICU で
 *   ラテン化する際 **中国語ピンイン** にしてしまう (例 小林病院→"xiǎo lín bìng yuàn") ため、
 *   これが「いろいろな言語が表示される」元凶になっていた。代わりに原語 `name` (日本では日本語) と
 *   実マッピングされた言語別タグ (name:ja / name:en / name:ja-Latn=正しいローマ字) のみ使う。
 *
 * - 取得不可 ('') : 日本語 + 英語のみ → name:ja → name:en → name。
 * - 日本語 ('ja') : name:ja → name (= 日本語に統一)。
 * - 英語   ('en') : name:en → name:ja-Latn(正ローマ字) → name。
 * - その他取得言語: name:<lang> → 英語 → ローマ字 → 日本語 → 原語。
 */
function localizedTextField(lang: string): ExpressionSpecification {
  const keys =
    lang === ''
      ? ['name:ja', 'name:en', 'name']
      : lang === 'ja'
        ? ['name:ja', 'name']
        : lang === 'en'
          ? ['name:en', 'name:ja-Latn', 'name']
          : [`name:${lang}`, 'name:en', 'name:ja-Latn', 'name:ja', 'name'];
  return [
    'coalesce',
    ...keys.map((k) => ['get', k] as ExpressionSpecification),
  ] as ExpressionSpecification;
}

/**
 * OMT (OpenMapTiles) schema の完全レイヤースタック。**下→上の描画順** で並べる:
 *   背景 → 土地被覆(緑) → 公園 → 土地利用 → 水域 → 水路 → 建物 → 道路(縁取り→本体)
 *   → 道路名 → 地名 → POI/施設名。
 * これが「面が建物の上に被さって地面一色に見える / 建物・施設名が出ない」を解消する要点。
 * source-layer 名・各レイヤーの minzoom は OMT メタデータ実測値に準拠
 * (building z13 / poi z12 / housenumber z14 / landcover z7 / transportation z4 等)。
 */
function omtLayers(lang: string): LayerSpecification[] {
  const tf = localizedTextField(lang);
  // 道路の太さ (class 別 + zoom)。縁取りは本体より少し太く描く。
  const roadWidth: DataDrivenPropertyValueSpecification<number> = [
    'interpolate',
    ['linear'],
    ['zoom'],
    9,
    ['match', ['get', 'class'], ['motorway', 'trunk'], 1.2, 'primary', 0.7, 0.15],
    13,
    [
      'match',
      ['get', 'class'],
      ['motorway', 'trunk'],
      3,
      'primary',
      2,
      ['secondary', 'tertiary'],
      1.3,
      0.6,
    ],
    16,
    [
      'match',
      ['get', 'class'],
      ['motorway', 'trunk'],
      8,
      'primary',
      6,
      ['secondary', 'tertiary'],
      4,
      ['minor', 'service'],
      2.4,
      1.6,
    ],
    19,
    [
      'match',
      ['get', 'class'],
      ['motorway', 'trunk'],
      18,
      'primary',
      14,
      ['secondary', 'tertiary'],
      10,
      6,
    ],
  ];

  return [
    { id: 'background', type: 'background', paint: { 'background-color': '#f4f2ee' } },
    {
      id: 'landcover',
      source: PMTILES_SOURCE,
      'source-layer': 'landcover',
      type: 'fill',
      paint: {
        'fill-color': [
          'match',
          ['get', 'class'],
          ['wood', 'forest'],
          '#c7dfb3',
          ['grass', 'meadow', 'grassland'],
          '#d8ebc4',
          'scrub',
          '#d0e4ba',
          'wetland',
          '#d2e7df',
          'sand',
          '#efe6cf',
          ['ice', 'glacier'],
          '#e8eff3',
          '#dceaca',
        ],
        'fill-opacity': 0.7,
      },
    },
    {
      id: 'park',
      source: PMTILES_SOURCE,
      'source-layer': 'park',
      type: 'fill',
      paint: { 'fill-color': '#d4ecc6', 'fill-opacity': 0.6 },
    },
    {
      id: 'landuse',
      source: PMTILES_SOURCE,
      'source-layer': 'landuse',
      type: 'fill',
      minzoom: 8,
      paint: {
        'fill-color': [
          'match',
          ['get', 'class'],
          ['residential', 'suburb', 'neighbourhood', 'quarter'],
          '#eae6df',
          ['commercial', 'retail'],
          '#f1e7e2',
          ['industrial', 'railway'],
          '#e7e5ec',
          'hospital',
          '#f3e3e1',
          ['school', 'university', 'college', 'kindergarten'],
          '#ede9d6',
          'cemetery',
          '#dbe5cf',
          '#ece8e0',
        ],
        'fill-opacity': 0.55,
      },
    },
    {
      id: 'water',
      source: PMTILES_SOURCE,
      'source-layer': 'water',
      type: 'fill',
      paint: { 'fill-color': '#a8d4f0' },
    },
    {
      id: 'waterway',
      source: PMTILES_SOURCE,
      'source-layer': 'waterway',
      type: 'line',
      paint: {
        'line-color': '#a8d4f0',
        'line-width': ['interpolate', ['linear'], ['zoom'], 9, 0.5, 14, 1.2, 18, 3],
      },
    },
    {
      id: 'building',
      source: PMTILES_SOURCE,
      'source-layer': 'building',
      type: 'fill',
      minzoom: 13,
      paint: {
        'fill-color': '#e2ddd2',
        'fill-outline-color': '#cdc6b6',
        'fill-opacity': ['interpolate', ['linear'], ['zoom'], 13, 0.4, 16, 0.92],
      },
    },
    {
      // 道路の縁取り (本体の下、視認性のため)。zoom 11 以降のみ。
      id: 'road-casing',
      source: PMTILES_SOURCE,
      'source-layer': 'transportation',
      type: 'line',
      minzoom: 11,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': '#cfc8ba',
        'line-gap-width': roadWidth,
        'line-width': 1,
      },
    },
    {
      id: 'road',
      source: PMTILES_SOURCE,
      'source-layer': 'transportation',
      type: 'line',
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': [
          'match',
          ['get', 'class'],
          ['motorway', 'trunk'],
          '#f6c87a',
          'primary',
          '#fbdca6',
          ['secondary', 'tertiary'],
          '#fdf6e3',
          '#ffffff',
        ],
        'line-width': roadWidth,
      },
    },
    {
      // 道路名ラベル (線に沿って配置)。
      id: 'road-name',
      source: PMTILES_SOURCE,
      'source-layer': 'transportation_name',
      type: 'symbol',
      minzoom: 14,
      layout: {
        'symbol-placement': 'line',
        'text-field': tf,
        'text-font': LABEL_FONT,
        'text-size': ['interpolate', ['linear'], ['zoom'], 14, 10, 18, 13],
      },
      paint: {
        'text-color': '#5a5650',
        'text-halo-color': 'rgba(255,255,255,0.9)',
        'text-halo-width': 1.2,
      },
    },
    {
      // 地名ラベル (国/都道府県/市/町/字)。rank が小さいほど主要。
      id: 'place-label',
      source: PMTILES_SOURCE,
      'source-layer': 'place',
      type: 'symbol',
      // 名前を持つ地名のみ。rank cap は外し、collision + symbol-sort-key(rank) で主要地名から
      // 詰めて表示 (cap 16 だと町丁目など高 rank の地名が出なかった)。
      filter: ['any', ['has', 'name:ja'], ['has', 'name'], ['has', 'name:latin']],
      layout: {
        'text-field': tf,
        'text-font': LABEL_FONT,
        'text-size': [
          'interpolate',
          ['linear'],
          ['zoom'],
          6,
          ['match', ['get', 'class'], ['city', 'town'], 13, 11],
          12,
          ['match', ['get', 'class'], ['city', 'town'], 16, 13],
          16,
          ['match', ['get', 'class'], ['city', 'town'], 19, 15],
        ],
        'text-max-width': 8,
        'text-padding': 3,
        'symbol-sort-key': ['coalesce', ['get', 'rank'], 99],
      },
      paint: {
        'text-color': '#2f3b46',
        'text-halo-color': 'rgba(255,255,255,0.92)',
        'text-halo-width': 1.5,
      },
    },
    {
      // 施設名 (POI = 店舗 / 公共施設 / 駅等)。「建物名称」相当を z15〜 で表示。
      id: 'poi-label',
      source: PMTILES_SOURCE,
      'source-layer': 'poi',
      type: 'symbol',
      minzoom: 15,
      // 名前を持つ POI のみ。rank の hard cap は外し、衝突回避 (collision) + symbol-sort-key で
      // rank 順に詰めて表示する (cap が厳しすぎると施設名がほとんど出なかった)。
      filter: ['any', ['has', 'name:ja'], ['has', 'name'], ['has', 'name:latin']],
      layout: {
        'text-field': tf,
        'text-font': LABEL_FONT,
        'text-size': ['interpolate', ['linear'], ['zoom'], 15, 10, 18, 12.5],
        'text-max-width': 7,
        'text-anchor': 'top',
        'text-offset': [0, 0.4],
        'symbol-sort-key': ['coalesce', ['get', 'rank'], 99],
      },
      paint: {
        'text-color': '#6a6258',
        'text-halo-color': 'rgba(255,255,255,0.9)',
        'text-halo-width': 1.2,
      },
    },
  ];
}

/**
 * Protomaps schema のレイヤースタック (planet fallback 用)。OMT ほど細分化しないが、
 * 背景 → 土地 → 土地利用 → 水域 → 建物 → 道路 → 地名 → POI の順で同様の構成にする。
 * source-layer 名は protomaps basemap 準拠 (earth / landuse / water / buildings / roads /
 * places / pois、rank は pmap:rank)。
 */
function protomapsLayers(lang: string): LayerSpecification[] {
  const tf = localizedTextField(lang);
  return [
    { id: 'background', type: 'background', paint: { 'background-color': '#f4f2ee' } },
    {
      id: 'earth',
      source: PMTILES_SOURCE,
      'source-layer': 'earth',
      type: 'fill',
      paint: { 'fill-color': '#f4f2ee' },
    },
    {
      id: 'landuse',
      source: PMTILES_SOURCE,
      'source-layer': 'landuse',
      type: 'fill',
      paint: {
        'fill-color': [
          'match',
          ['get', 'pmap:kind'],
          ['park', 'forest', 'wood', 'grass', 'meadow', 'recreation_ground', 'nature_reserve'],
          '#d4ecc6',
          ['residential', 'neighbourhood'],
          '#eae6df',
          '#e9e5dd',
        ],
        'fill-opacity': 0.6,
      },
    },
    {
      id: 'water',
      source: PMTILES_SOURCE,
      'source-layer': 'water',
      type: 'fill',
      paint: { 'fill-color': '#a8d4f0' },
    },
    {
      id: 'building',
      source: PMTILES_SOURCE,
      'source-layer': 'buildings',
      type: 'fill',
      minzoom: 13,
      paint: {
        'fill-color': '#e2ddd2',
        'fill-outline-color': '#cdc6b6',
        'fill-opacity': ['interpolate', ['linear'], ['zoom'], 13, 0.4, 16, 0.92],
      },
    },
    {
      id: 'road',
      source: PMTILES_SOURCE,
      'source-layer': 'roads',
      type: 'line',
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': [
          'match',
          ['get', 'pmap:kind'],
          ['highway', 'major_road'],
          '#f6c87a',
          'medium_road',
          '#fdf6e3',
          '#ffffff',
        ],
        'line-width': [
          'interpolate',
          ['linear'],
          ['zoom'],
          9,
          ['match', ['get', 'pmap:kind'], ['highway', 'major_road'], 1.2, 0.3],
          14,
          ['match', ['get', 'pmap:kind'], ['highway', 'major_road'], 3, 'medium_road', 1.6, 0.9],
          18,
          ['match', ['get', 'pmap:kind'], ['highway', 'major_road'], 9, 'medium_road', 5, 2.5],
        ],
      },
    },
    {
      id: 'place-label',
      source: PMTILES_SOURCE,
      'source-layer': 'places',
      type: 'symbol',
      filter: ['any', ['has', 'name:ja'], ['has', 'name']],
      layout: {
        'text-field': tf,
        'text-font': LABEL_FONT,
        'text-size': ['interpolate', ['linear'], ['zoom'], 6, 11, 12, 14, 16, 16],
        'text-max-width': 8,
        'text-padding': 3,
        'symbol-sort-key': ['coalesce', ['get', 'pmap:rank'], 99],
      },
      paint: {
        'text-color': '#2f3b46',
        'text-halo-color': 'rgba(255,255,255,0.92)',
        'text-halo-width': 1.5,
      },
    },
    {
      id: 'poi-label',
      source: PMTILES_SOURCE,
      'source-layer': 'pois',
      type: 'symbol',
      minzoom: 15,
      layout: {
        'text-field': tf,
        'text-font': LABEL_FONT,
        'text-size': ['interpolate', ['linear'], ['zoom'], 15, 10, 18, 12.5],
        'text-max-width': 7,
        'text-anchor': 'top',
        'text-offset': [0, 0.4],
      },
      paint: {
        'text-color': '#6a6258',
        'text-halo-color': 'rgba(255,255,255,0.9)',
        'text-halo-width': 1.2,
      },
    },
  ];
}

/**
 * pmtiles (オフライン) ベクタースタイルを構築する。
 *
 * @param pmtilesUrl `pmtiles://<blob|remote url>` 形式 (addPmtilesSource() の戻り値)。
 * @param schema pmtiles の vector schema ('protomaps' | 'omt')。district.mapPmtiles.schema を渡す。
 *   既定は 'protomaps' (後方互換 / schema 不明時のフォールバック)。
 * @returns glyphs 付き + 地名 symbol 付きの schema 対応スタイル。
 */
export function buildPmtilesStyle(
  pmtilesUrl: string,
  schema: PmtilesSchema = 'protomaps',
  lang: string = resolveLabelLang(),
): StyleSpecification {
  return {
    version: 8,
    // docs/107: glyphs は Protomaps CDN。これが無いと text-field を持つ symbol layer が描画されない。
    glyphs: PROTOMAPS_GLYPHS,
    sources: {
      [PMTILES_SOURCE]: {
        type: 'vector',
        url: pmtilesUrl,
        attribution: PMTILES_ATTRIBUTION,
      },
    },
    layers: schema === 'omt' ? omtLayers(lang) : protomapsLayers(lang),
  };
}
